import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { catchError, of } from 'rxjs';
import { CHANNEL_TYPES_OBJECT } from '../../../../../enum/constants';
import { ChannelService } from '../../../../../services/channel.service';
import { ChannelTypeApiItem } from '../../../../../modules/crm/channel/components/channel/channel.interface';
import {
  CHANNEL_TYPE_GROUP,
  CHANNEL_TYPE_GROUPS_ORDER,
  CHANNEL_TYPE_GROUP_TITLES,
  ChannelTypeOption,
  ChannelTypesGroup,
  ChannelTypesPayload,
  ChannelTypesResult,
} from './channel-types.interface';

@Component({
  selector: 'app-channel-types',
  templateUrl: './channel-types.component.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
  host: { class: 'block h-full min-h-0' },
})
export class ChannelTypesComponent implements OnInit {
  private readonly channelService = inject(ChannelService);

  @Input() payload: ChannelTypesPayload = {};

  @Output() close = new EventEmitter<ChannelTypesResult | void>();

  protected groups: ChannelTypesGroup[] = [];
  protected isLoading = false;

  ngOnInit(): void {
    this.loadTypes();
  }

  protected get selectedType(): string | null {
    return this.payload?.selectedType ?? null;
  }

  protected isSelected(option: ChannelTypeOption): boolean {
    return this.selectedType === option.value;
  }

  protected selectType(option: ChannelTypeOption): void {
    if (option.disabled) return;

    this.close.emit({ type: option.value });
  }

  protected cancel(): void {
    this.close.emit();
  }

  private loadTypes(): void {
    this.isLoading = true;
    this.channelService
      .getChannelTypes()
      .pipe(catchError(() => of<ChannelTypeApiItem[]>([])))
      .subscribe((items) => {
        this.isLoading = false;
        this.groups = this.groupOptions(this.mapApiTypes(items));
      });
  }

  private mapApiTypes(items: ChannelTypeApiItem[]): ChannelTypeOption[] {
    return items.map((item) => {
      // Names and logos live on the frontend, the API only tells which types exist
      const meta = CHANNEL_TYPES_OBJECT[item.key];

      return {
        value: item.key,
        name: meta?.name || item.key,
        img: meta?.img_url || '',
        group: this.resolveGroup(item.group),
        position: item.position,
        disabled: item.disabled,
      };
    });
  }

  /** Groups unknown to the frontend fall into `OTHER` */
  private resolveGroup(group?: string): CHANNEL_TYPE_GROUP {
    const normalizedGroup = (group || '').trim().toUpperCase();

    return CHANNEL_TYPE_GROUPS_ORDER.find((item) => String(item) === normalizedGroup) ?? CHANNEL_TYPE_GROUP.OTHER;
  }

  private groupOptions(options: ChannelTypeOption[]): ChannelTypesGroup[] {
    return CHANNEL_TYPE_GROUPS_ORDER.map((group) => ({
      key: group,
      title: CHANNEL_TYPE_GROUP_TITLES[group],
      items: options.filter((option) => option.group === group).sort((a, b) => a.position - b.position),
    })).filter((group) => group.items.length > 0);
  }
}
